import { useState, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/hooks/useAuth";

type MFAStatus = "loading" | "no_user" | "needs_setup" | "needs_verify" | "verified";

export const useMFA = () => {
  const { user, loading: authLoading } = useAuth();
  const [mfaStatus, setMfaStatus] = useState<MFAStatus>("loading");

  useEffect(() => {
    if (authLoading) {
      setMfaStatus("loading");
      return;
    }

    if (!user) {
      setMfaStatus("no_user");
      return;
    }

    checkMFA();
  }, [user, authLoading]);

  const checkMFA = async () => {
    try {
      // Check localStorage first — if verified within 3 months, skip everything
      const mfaData = localStorage.getItem("mfa_verified");
      if (mfaData) {
        try {
          const parsed = JSON.parse(mfaData);
          const threeMonths = 90 * 24 * 60 * 60 * 1000;
          if (parsed.userId === user?.id && Date.now() - parsed.timestamp < threeMonths) {
            setMfaStatus("verified");
            return;
          }
        } catch {
          // Invalid data, remove it
        }
        localStorage.removeItem("mfa_verified");
      }

      // Check if user has any verified TOTP factors
      const { data: factors, error } = await supabase.auth.mfa.listFactors();
      
      if (error) {
        console.error("MFA check error:", error);
        setMfaStatus("needs_setup");
        return;
      }

      const verifiedFactor = factors?.totp?.find((f) => f.status === "verified");

      if (!verifiedFactor) {
        setMfaStatus("needs_setup");
        return;
      }

      // User has a verified factor but no valid localStorage flag — needs verification
      setMfaStatus("needs_verify");
    } catch (error) {
      console.error("MFA check error:", error);
      setMfaStatus("needs_setup");
    }
  };

  return { mfaStatus, recheckMFA: checkMFA };
};
